import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { IContextSheetContentProps, ISheetConfig, DRAWER_SCROLL_CLASSES } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Input, InputField } from '@/components/gluestack/input';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Button, ButtonText, ButtonSpinner } from '@/components/gluestack/button';
import { Switch } from '@/components/gluestack/switch';
import { Pressable } from '@/components/gluestack/pressable';
import { ContextSheet } from '@/components/ui/ContextSheet';
import DatePickerSheet, { TimeValue } from '@/components/ui/DatePickerSheet';
import LocationPlacePicker from '@/components/ui/LocationPlacePicker';
import { EventsService, EventCreateParams, EventUpdateParams } from '~/services/domain/EventsService';
import { EventDetails } from '~/models/EventDetails';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryClient } from '@tanstack/react-query';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { Calendar03Icon, Cancel01Icon, Clock01Icon, Location01Icon } from '@hugeicons-pro/core-stroke-rounded';
import dayjs from '@/i18n/dayjs-config';
import { useTranslation } from 'react-i18next';
import { PlaceSearchResult } from '@/components/providers/LocationProvider';

function formatTimeValue(time: TimeValue): string {
  const date = new Date(2000, 0, 1, time.hours, time.minutes);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}

function extractTimeFromDate(date: Date): TimeValue | null {
  const h = date.getHours();
  const m = date.getMinutes();
  if (h === 0 && m === 0) return null;
  return { hours: h, minutes: m };
}

function buildDateTime(date: Date, time: TimeValue | null): dayjs.Dayjs {
  return time
    ? dayjs(date).hour(time.hours).minute(time.minutes).second(0).millisecond(0)
    : dayjs(date).startOf('day');
}

export interface IAddEditEventProps extends IContextSheetContentProps {
  payload?: {
    event?: EventDetails;
    onEventSaved?: (result?: { Id: number; Code: string }) => void;
  };
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <VStack className="gap-1.5">
      <Text size="sm" bold className="text-typography-700">{label}</Text>
      {children}
      {error && <Text size="xs" className="text-error-600">{error}</Text>}
    </VStack>
  );
}

function ClearButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      onPress={(e) => {
        e.stopPropagation();
        onPress();
      }}
      className="p-0.5 active:opacity-50 web:cursor-pointer"
      hitSlop={8}>
      <HugeIconsWrapper icon={Cancel01Icon} size={16} className="text-typography-400" />
    </Pressable>
  );
}

export function AddEditEvent({ onClose, payload }: IAddEditEventProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const queryClient = useQueryClient();

  const existingEvent = payload?.event;
  const isEditing = !!existingEvent;

  const [code, setCode] = useState(existingEvent?.Code ?? '');
  const [name, setName] = useState(existingEvent?.Name ?? '');
  const [details, setDetails] = useState(existingEvent?.Details ?? '');
  const [address, setAddress] = useState(existingEvent?.Address ?? '');
  const [website, setWebsite] = useState(existingEvent?.Website ?? '');
  const [startDate, setStartDate] = useState<Date | null>(
    existingEvent?.StartDate ? new Date(existingEvent.StartDate) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    existingEvent?.EndDate ? new Date(existingEvent.EndDate) : null
  );
  const [startTime, setStartTime] = useState<TimeValue | null>(
    existingEvent?.TimesShown && existingEvent?.StartDate
      ? extractTimeFromDate(new Date(existingEvent.StartDate))
      : null
  );
  const [endTime, setEndTime] = useState<TimeValue | null>(
    existingEvent?.TimesShown && existingEvent?.EndDate
      ? extractTimeFromDate(new Date(existingEvent.EndDate))
      : null
  );
  const [creatorIsOrganizer, setCreatorIsOrganizer] = useState(existingEvent?.CreatorIsOrganizer ?? false);
  const [locationLatLng, setLocationLatLng] = useState<string>('');
  const [locationDisplay, setLocationDisplay] = useState(existingEvent?.PlaceName ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // If an end date is set, both times must be set or neither
  const hasTimesMismatch = endDate !== null && (startTime === null) !== (endTime === null);

  const hasLocation = locationLatLng.length > 0 || locationDisplay.length > 0;
  const canSubmit = code.trim().length > 0 && name.trim().length > 0 && startDate !== null && hasLocation && !hasTimesMismatch;

  const handleLocationSelect = (place: PlaceSearchResult) => {
    setLocationLatLng(`${place.latitude},${place.longitude}`);
    setLocationDisplay(place.displayName);
    setShowLocationPicker(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      setShowErrors(true);
      return;
    }
    setIsSubmitting(true);

    const timesShown = startTime !== null || endTime !== null;
    const startDateTime = buildDateTime(startDate!, startTime);
    const endDateTime = endDate ? buildDateTime(endDate, endTime) : null;
    const normalizedWebsite = website.trim()
      ? website.trim().startsWith('https://') ? website.trim() : `https://${website.trim()}`
      : '';

    try {
      if (isEditing && existingEvent) {
        const updateData: EventUpdateParams = {
          Name: name.trim(),
          Details: details,
          TimesShown: timesShown,
          Address: address,
          Website: normalizedWebsite,
          CreatorIsOrganizer: creatorIsOrganizer,
        };
        updateData.StartDate = startDateTime.toISOString();
        if (endDateTime) updateData.EndDate = endDateTime.toISOString();
        if (!endDate && existingEvent.EndDate) updateData.ClearEndDate = true;
        if (locationLatLng) updateData.LocationLatLng = locationLatLng;

        await EventsService.updateEvent(existingEvent.Id, updateData);
        showSuccessToast(t('events.updateSuccess'));
        queryClient.invalidateQueries({ queryKey: ['events'] });
        queryClient.invalidateQueries({ queryKey: ['member-details', existingEvent.Code] });
        payload?.onEventSaved?.();
      } else {
        const createData: EventCreateParams = {
          Code: code.trim(),
          Name: name.trim(),
          Details: details,
          StartDate: startDateTime.toISOString(),
          TimesShown: timesShown,
          Address: address,
          Website: normalizedWebsite,
          CreatorIsOrganizer: creatorIsOrganizer,
        };
        if (endDateTime) createData.EndDate = endDateTime.toISOString();
        if (locationLatLng) createData.LocationLatLng = locationLatLng;

        const result = await EventsService.createEvent(createData);
        showSuccessToast(t('events.createSuccess'));
        queryClient.invalidateQueries({ queryKey: ['events'] });
        payload?.onEventSaved?.(result);
      }
      onClose?.();
    } catch (e: any) {
      showErrorToast(
        isEditing ? t('events.updateError') : t('events.createError'),
        e
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <View style={{ flex: 1 }}>
        <ScrollView
          style={{ flex: 1 }}
          {...DRAWER_SCROLL_CLASSES}
          contentContainerStyle={{ paddingBottom: 16 }}
          keyboardShouldPersistTaps="handled"
        >
        <VStack className="gap-4 px-4 py-4">
          <FormField label={t('events.form.code')} error={showErrors && !code.trim() ? t('events.form.required') : undefined}>
            <Input size="md" isReadOnly={isEditing} isDisabled={isEditing} isInvalid={showErrors && !code.trim()}>
              <InputField
                value={code}
                onChangeText={setCode}
                placeholder={t('events.form.codePlaceholder')}
                autoCapitalize="none"
                editable={!isEditing}
              />
            </Input>
            {!isEditing && <Text size="xs" className="text-typography-500">{t('events.form.codeHint')}</Text>}
          </FormField>

          <FormField label={t('events.form.name')} error={showErrors && !name.trim() ? t('events.form.required') : undefined}>
            <Input size="md" isInvalid={showErrors && !name.trim()}>
              <InputField
                value={name}
                onChangeText={setName}
                placeholder={t('events.form.namePlaceholder')}
              />
            </Input>
            <Text size="xs" className="text-typography-500">{t('events.form.nameHint')}</Text>
          </FormField>

          <FormField label={t('events.form.startDate')} error={showErrors && !startDate ? t('events.form.required') : undefined}>
            <HStack className="gap-2">
              <Pressable onPress={() => setShowStartDatePicker(true)} className="flex-[3]">
                <HStack className={`items-center gap-2 rounded-lg border px-3 py-2.5 ${showErrors && !startDate ? 'border-error-700' : 'border-outline-300'}`}>
                  <HugeIconsWrapper icon={Calendar03Icon} size={18} className="text-typography-500" />
                  <Text className={startDate ? 'text-typography-900 flex-1' : 'text-typography-400 flex-1'} numberOfLines={1}>
                    {startDate ? dayjs(startDate).format('ll') : t('events.form.selectDate')}
                  </Text>
                </HStack>
              </Pressable>
              <Pressable onPress={() => setShowStartTimePicker(true)} className="flex-[2]">
                <HStack className="items-center gap-2 rounded-lg border border-outline-300 px-3 py-2.5">
                  <HugeIconsWrapper icon={Clock01Icon} size={18} className="text-typography-500" />
                  <Text className={startTime ? 'text-typography-900 flex-1' : 'text-typography-400 flex-1'} numberOfLines={1}>
                    {startTime ? formatTimeValue(startTime) : t('events.form.selectTime')}
                  </Text>
                  {startTime && <ClearButton onPress={() => setStartTime(null)} />}
                </HStack>
              </Pressable>
            </HStack>
          </FormField>

          <FormField label={t('events.form.endDate')}>
            <HStack className="gap-2">
              <Pressable onPress={() => setShowEndDatePicker(true)} className="flex-[3]">
                <HStack className="items-center gap-2 rounded-lg border border-outline-300 px-3 py-2.5">
                  <HugeIconsWrapper icon={Calendar03Icon} size={18} className="text-typography-500" />
                  <Text className={endDate ? 'text-typography-900 flex-1' : 'text-typography-400 flex-1'} numberOfLines={1}>
                    {endDate ? dayjs(endDate).format('ll') : t('events.form.selectDate')}
                  </Text>
                  {endDate && <ClearButton onPress={() => { setEndDate(null); setEndTime(null); }} />}
                </HStack>
              </Pressable>
              <Pressable onPress={() => setShowEndTimePicker(true)} className="flex-[2]">
                <HStack className="items-center gap-2 rounded-lg border border-outline-300 px-3 py-2.5">
                  <HugeIconsWrapper icon={Clock01Icon} size={18} className="text-typography-500" />
                  <Text className={endTime ? 'text-typography-900 flex-1' : 'text-typography-400 flex-1'} numberOfLines={1}>
                    {endTime ? formatTimeValue(endTime) : t('events.form.selectTime')}
                  </Text>
                  {endTime && <ClearButton onPress={() => setEndTime(null)} />}
                </HStack>
              </Pressable>
            </HStack>
          </FormField>

          {hasTimesMismatch && (
            <Text size="sm" className="text-error-600">{t('events.form.timesError')}</Text>
          )}

          <FormField label={t('events.form.location')} error={showErrors && !hasLocation ? t('events.form.required') : undefined}>
            <Pressable onPress={() => setShowLocationPicker(true)}>
              <HStack className={`items-center gap-2 rounded-lg border px-3 py-2.5 ${showErrors && !hasLocation ? 'border-error-700' : 'border-outline-300'}`}>
                <HugeIconsWrapper icon={Location01Icon} size={18} className="text-typography-500" />
                <Text className={locationDisplay ? 'text-typography-900 flex-1' : 'text-typography-400 flex-1'} numberOfLines={1}>
                  {locationDisplay || t('events.form.selectLocation')}
                </Text>
              </HStack>
            </Pressable>
          </FormField>

          <FormField label={t('events.form.venue')}>
            <Input size="md">
              <InputField
                value={address}
                onChangeText={setAddress}
                placeholder={t('events.form.venuePlaceholder')}
              />
            </Input>
          </FormField>

          <FormField label={t('events.form.website')}>
            <Input size="md">
              <InputField
                value={website}
                onChangeText={setWebsite}
                placeholder={t('events.form.websitePlaceholder')}
                autoCapitalize="none"
                keyboardType="url"
              />
            </Input>
          </FormField>

          <FormField label={t('events.form.details')}>
            <Textarea size="md" className="h-48">
              <TextareaInput
                value={details}
                onChangeText={setDetails}
                placeholder={t('events.form.detailsPlaceholder')}
                textAlignVertical="top"
                multiline
              />
            </Textarea>
          </FormField>

          <HStack className="items-center justify-between">
            <Text size="sm" bold className="text-typography-700">{t('events.form.organizer')}</Text>
            <Switch value={creatorIsOrganizer} onValueChange={setCreatorIsOrganizer} />
          </HStack>
        </VStack>
        </ScrollView>

        <View className="px-4 pt-3 border-t border-outline-100">
          <Button
            size="lg"
            onPress={handleSubmit}
            isDisabled={isSubmitting}>
            {isSubmitting && <ButtonSpinner className="mr-2" />}
            <ButtonText>
              {isEditing ? t('events.form.save') : t('events.form.create')}
            </ButtonText>
          </Button>
        </View>
      </View>

      <ContextSheet
        isOpen={showStartDatePicker}
        onClose={() => setShowStartDatePicker(false)}
        contentComponent={DatePickerSheet}
        title={t('events.form.startDate')}
        payload={{
          mode: 'date',
          selectedDate: startDate ?? new Date(),
          minDate: new Date(),
          maxDate: dayjs().add(5, 'year').toDate(),
          onDateChange: (date: Date) => {
            setStartDate(date);
            setShowStartDatePicker(false);
          },
        }}
      />

      <ContextSheet
        isOpen={showEndDatePicker}
        onClose={() => setShowEndDatePicker(false)}
        contentComponent={DatePickerSheet}
        title={t('events.form.endDate')}
        payload={{
          mode: 'date',
          selectedDate: endDate ?? (startDate ? dayjs(startDate).add(1, 'day').toDate() : null),
          minDate: startDate || new Date(),
          maxDate: dayjs().add(5, 'year').toDate(),
          onDateChange: (date: Date) => {
            setEndDate(date);
            setShowEndDatePicker(false);
          },
        }}
      />

      <ContextSheet
        isOpen={showStartTimePicker}
        onClose={() => setShowStartTimePicker(false)}
        contentComponent={DatePickerSheet}
        title={t('events.form.startTime')}
        payload={{
          mode: 'time',
          selectedTime: startTime,
          onTimeChange: (time: TimeValue) => {
            setStartTime(time);
            setShowStartTimePicker(false);
          },
        }}
      />

      <ContextSheet
        isOpen={showEndTimePicker}
        onClose={() => setShowEndTimePicker(false)}
        contentComponent={DatePickerSheet}
        title={t('events.form.endTime')}
        payload={{
          mode: 'time',
          selectedTime: endTime,
          onTimeChange: (time: TimeValue) => {
            setEndTime(time);
            setShowEndTimePicker(false);
          },
        }}
      />

      <ContextSheet
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        contentComponent={LocationPlacePicker}
        title={t('events.form.location')}
        payload={{
          onSelect: handleLocationSelect,
          autoFocus: true,
        }}
      />
    </>
  );
}

AddEditEvent.sheetConfig = {
  largeScreenMode: 'drawer',
  size: 'md',
  height: 'full',
  scrollableContent: true,
} satisfies ISheetConfig;
